import React, { useEffect, useRef } from 'react';
import shaka from 'shaka-player';
import { isMobile, isTablet, isDesktop, useMobileOrientation } from 'react-device-detect';
import { useSelector, useDispatch } from 'react-redux';
import { getVideo } from './hooks/getVideo';
import { useHoveringListener } from './hooks/hoveringListener';
import { useFullscreenListener } from './hooks/fullscreenListener';
import { setBooleanState, setCurrentTime, setTotalDuration, setVideoUrl, setExpirationTime, setSettingsView, setCurrentVideoPosition, setCurrentVideoMetaData, setVideoTitle } from '../../features/videoPlayer/videoPlayer.slice';

import TopPlayerButtons from './playerComponents/TopPlayerButtons';
import MidPlayerButtons from './playerComponents/MidPlayerButtons';
import BottomPlayerButtons from './playerComponents/BottomPlayerButtons';
import PlayerLoader from './playerComponents/PlayerLoader';
import KeydownPlayerToasters from './playerComponents/KeydownPlayerToasters';

const VideoPlayer = ({ id, title, thumbnail }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const intervalRef = useRef(null);
  const cleanupPromise = useRef(null); // Track cleanup promise
  const savedPlaybackPosition = useRef(0);
  const wasPlayingBeforeRefresh = useRef(false);

  const videoId = useSelector((state) => state.videoPlayer.videoId);
  const volume = useSelector((state) => state.videoPlayer.volume);
  const hovering = useSelector((state) => state.videoPlayer.hovering);
  const videoUrl = useSelector((state) => state.videoPlayer.videoUrl);
  const flattenedCourse = useSelector((state) => state.videoPlayer.flattenedCourse);
  const expirationTime = useSelector((state) => state.videoPlayer.expirationTime);
  const initialVideoLoading = useSelector((state) => state.videoPlayer.initialVideoLoading);
  const isInteracting = useSelector((state) => state.videoPlayer.isInteracting);
  const isBuffering = useSelector((state) => state.videoPlayer.isBuffering);

  const dispatch = useDispatch();

  // useLoadVideo(videoRef, playerRef, wasPlayingBeforeRefresh, savedPlaybackPosition, cleanupPromise, intervalRef);
  // useHandleClickListener();
  // useHandleKeyListener();
  useHoveringListener(isMobile, isTablet);
  useFullscreenListener();

    useEffect(() => {
      const initializePlayer = async () => {
          const video = videoRef.current;

          await cleanupPlayer();

          if (videoUrl) {
              const player = new shaka.Player();
              playerRef.current = player;

              player.addEventListener('error', (event) => {
                console.error('Shaka Player Error:', event.detail);
              });

              player.addEventListener('buffering', onBuffering);

              try {
              await player.attach(video);

              const videoPosition = flattenedCourse.findIndex(video => video.id === videoId);
              dispatch(setCurrentVideoPosition(videoPosition));
              dispatch(setCurrentVideoMetaData(flattenedCourse[videoPosition]));
              dispatch(setVideoTitle(flattenedCourse[videoPosition]?.name || 'Title Not Found'));

              const startTime = savedPlaybackPosition.current || 0;

              await player.load(videoUrl, startTime);
              
              //setIsPlayerInitialized(true);
              dispatch(setTotalDuration(video.duration));

              if (wasPlayingBeforeRefresh.current) {
                  try {
                    await video.play();
                  } catch (err) {
                    console.error('Error resuming playback:', err);
                  }
              }

              intervalRef.current = setInterval(() => {
                  const currentTime = Date.now();
                  const timeLeft = expirationTime - currentTime;

                  // reloads presigned URL if less than 10 minutes left if the user is still watching
                  if (timeLeft < 10 * 60 * 1000) { // Check if less than 10 minutes left
                      const { videoUrl, expirationTime: newExpirationTime } = getVideo(videoId);
                      if (videoUrl && newExpirationTime) {
                          dispatch(setVideoUrl(videoUrl));
                          dispatch(setExpirationTime(newExpirationTime));
                      }
                  }
              }, 5 * 60 * 1000); // Check every 5 minutes

              } catch (error) {
              console.error('Error during player initialization/load:', error);
              console.error('Error code:', error.code);
              console.error('Error message:', error.message);
              console.error('Error stack:', error.stack);
              }
          }
      };

      initializePlayer();

      return () => {
        cleanupPlayer();
      };
    }, [videoUrl]);
  
  const cleanupPlayer = async () => {
      // If cleanup is already running, wait for it to finish
      if (cleanupPromise.current) {
        await cleanupPromise.current;
        return;
      }
  
      // Create a new cleanup promise
      cleanupPromise.current = (async () => {
        const video = videoRef.current;
        if (video) {
          savedPlaybackPosition.current = video.currentTime || 0;
          wasPlayingBeforeRefresh.current = !video.paused && !video.ended;
        }
  
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
  
        if (playerRef.current) {
          try {
            await playerRef.current.destroy();
            playerRef.current.removeEventListener('buffering', onBuffering);
          } catch (error) {
            console.error('Error destroying player:', error);
          }
          playerRef.current = null;
          // setIsPlayerInitialized(false);
        }
  
      })();
  
      await cleanupPromise.current;
      cleanupPromise.current = null;
  };
  
  const onBuffering = (event) => {
      dispatch(setBooleanState({ key: 'isBuffering', value: event.buffering }));
  };

  useEffect(() => {
    const video = videoRef.current;
    video.volume = volume / 100;
  }, [volume]);

  useEffect(() => {
    if (!initialVideoLoading) {
      // reset currentTime in Redux store and video element time when videoId changes
      dispatch(setCurrentTime(0));
      savedPlaybackPosition.current = 0;
      videoRef.current.currentTime = 0;
      const { videoUrl, expirationTime } = getVideo(videoId);
      if (videoUrl && expirationTime) {
          dispatch(setVideoUrl(videoUrl));
          dispatch(setExpirationTime(expirationTime));
      }
    } else {
      // turn off initial loading flag after first load to avoid calling duplicate times
      dispatch(setBooleanState({ key: 'initialVideoLoading', value: false }));
    }
  }, [videoId]);


  useEffect(() => {
    const handleDocumentClick = (e) => {
      handleClickAway(e); // Ensure `handleClickAway` is called independently
    };

    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  const togglePlayPause = (videoRef) => {
      
      const video = videoRef.current;
  
      if (video.paused) {
        video.play();
        dispatch(setBooleanState({ key: 'isPlaying', value: true }));
      } else {
        video.pause();
        dispatch(setBooleanState({ key: 'isPlaying', value: false }));
      }
  };

  const handleClickAway = (e) => {
    
      if (isMobile || isTablet) return; // Skip for mobile devices
    
      const container = document.getElementById('player-container');
    
      if (container === e.target) {
        dispatch(setBooleanState({ key: 'hovering', value: false })); // Hide UI if clicked outside
        dispatch(setBooleanState({ key: 'showSettings', value: false })); // Hide settings if clicked outside
        dispatch(setSettingsView('main'));
      }
  };

const handleMouseMove = () => {
  
      if (isMobile || isTablet) return; // Skip for mobile devices

      dispatch(setBooleanState({ key: 'hovering', value: true })); // Show UI on mouse move
      dispatch(setBooleanState({ key: 'isInteracting', value: true })); // Set mouse moving state
      clearTimeout(window.mouseMoveTimeout); // Clear any existing timeout
      window.mouseMoveTimeout = setTimeout(() => {
        dispatch(setBooleanState({ key: 'isInteracting', value: false })); // Reset mouse moving state after delay
      }, 200); // Adjust delay as needed
};
  
const handleMouseLeave = () => {
      
      if (isMobile || isTablet) return; // Skip for mobile devices

      dispatch(setBooleanState({ key: 'hovering', value: false })); 
      dispatch(setBooleanState({ key: 'showSettings', value: false })); 
      dispatch(setSettingsView('main'));
};

const handleTouchStart = (event) => {
      // Check if the touch event target is a UI button

      const target = event.target;
      const isUIElement = target.closest('button') || target.closest('#progress-bar') || target.closest('#player-settings') || target.closest('#playback-speed-settings') || target.closest('#lesson-button'); // Add specific class names for your UI elements if needed

      // dispatch(setBooleanState({ key: 'isInteracting', value: true })); // Set touch interacting state
      clearTimeout(window.touchTimeout);
  
      window.touchTimeout = setTimeout(() => {
        dispatch(setBooleanState({ key: 'isInteracting', value: false })); // Reset touch interacting state after delay
      }, 200); // Adjust delay as needed
  
      if (isUIElement) {
        return; // Do not toggle hovering if interacting with a UI button
      }

      dispatch(setBooleanState({ key: 'hovering', value: !hovering })); // Show or hide UI on touch start
      dispatch(setBooleanState({ key: 'showSettings', value: false }));
      dispatch(setSettingsView('main'));
};

  const updateTime = () => {
    if (isInteracting) return; // Skip updating time if interacting with the slider
    const video = videoRef.current;
    dispatch(setCurrentTime(video.currentTime || 0));
    dispatch(setTotalDuration(video.duration || 0));
  };
  
  return (
    <div
      ref={containerRef}
      // className='video-player'
      style={{
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseMove={() => handleMouseMove()} // Detect mouse movement to show UI
      onMouseLeave={() => {handleMouseLeave()}} // Hide UI when mouse leaves
      onTouchStart={(e) => handleTouchStart(e)} // Mimics mouse movement on touch devices
      onClick={() => containerRef.current.focus()} // Ensure the div gains focus when clicked
      tabIndex="0"
    >
      <div
        id='player-container'
        className='video-player'
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundColor: hovering ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
          zIndex: 1,
        }}
      >
        
      </div>
        <video
          ref={videoRef}
          poster={thumbnail}
          className='video-player'
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            zIndex: 0,
          }}
          onTimeUpdate={updateTime}
          controls={false} // Disable default controls to use custom controls
          playsInline // For iOS to play videos inline
        >
          Your browser does not support the video tag.
        </video>
      {/* Player Controls and Settings Components */}
      <TopPlayerButtons videoRef={videoRef} containerRef={containerRef} isMobile={isMobile} isTablet={isTablet} />
      <MidPlayerButtons videoRef={videoRef} togglePlayPause={togglePlayPause} />
      <BottomPlayerButtons videoRef={videoRef} containerRef={containerRef} />
      <PlayerLoader isBuffering={isBuffering} />
      <KeydownPlayerToasters videoRef={videoRef} togglePlayPause={togglePlayPause} />
    </div>
  );
};

export default VideoPlayer;
